const { APPOINTMENT, SERVICE, SUMMARY, DISPLAY, PAYMENT, REFUND, END_REASON, numericStatus, statusText } = require('./photographyStatus.cjs');
const APPOINTMENT_GROUP_STATUSES = Object.freeze({
    all: [], payment: [DISPLAY.WAITING_PAYMENT], matching: [DISPLAY.WAITING_ACCEPTANCE],
    fulfillment: [DISPLAY.WAITING_FULFILLMENT], active: [DISPLAY.IN_SERVICE, DISPLAY.AWAITING_PHOTOS, DISPLAY.PROCESSING],
    ended: [DISPLAY.COMPLETED, DISPLAY.ENDED],
});

const APPOINTMENT_STATUS_TEXT = Object.freeze({ [APPOINTMENT.WAITING_PAYMENT]: '待支付', [APPOINTMENT.PAID]: '已支付', [APPOINTMENT.CLOSED]: '已关闭' });
const SUMMARY_STATUS_TEXT = Object.freeze({ [SUMMARY.WAITING_ACCEPTANCE]: '匹配中', [SUMMARY.WAITING_FULFILLMENT]: '待履约', [SUMMARY.IN_SERVICE]: '进行中', [SUMMARY.AWAITING_PHOTOS]: '待出片', [SUMMARY.COMPLETED]: '已完成', [SUMMARY.ENDED]: '已结束' });
const DISPLAY_STATUS_TEXT = Object.freeze({ [DISPLAY.WAITING_PAYMENT]: '待支付', [DISPLAY.WAITING_ACCEPTANCE]: '匹配中', [DISPLAY.WAITING_FULFILLMENT]: '待履约', [DISPLAY.IN_SERVICE]: '进行中', [DISPLAY.AWAITING_PHOTOS]: '待出片', [DISPLAY.COMPLETED]: '已完成', [DISPLAY.ENDED]: '已结束', [DISPLAY.PROCESSING]: '处理中' });

const asText = (value) => value === null || value === undefined ? "" : String(value);

const asMoney = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number.toFixed(2) : "0.00";
};

const getAppointmentStatusText = (status) => statusText(status, APPOINTMENT_STATUS_TEXT);
const getSummaryStatusText = (status) => statusText(status, SUMMARY_STATUS_TEXT);

const normalizeAppointmentDateTime = (value) => {
    const text = asText(value);
    // Zone-less API values are Shanghai wall-clock times, not the browser's local time.
    const wallTime = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text);
    if (!wallTime && !/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(text)) return "";
    const date = new Date(wallTime ? `${text.replace(" ", "T")}+08:00` : text);
    if (!Number.isFinite(date.getTime())) return "";
    const shanghai = new Date(date.getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 19).replace("T", " ");
    return wallTime && shanghai !== text ? "" : shanghai;
};
const formatAppointmentDateTime = (value) => normalizeAppointmentDateTime(value).slice(0, 16);
const formatServicePlan = (start, end) => {
    if (!start || !end || end <= start) return "计划时段暂不可用，请刷新确认";
    return `${formatAppointmentDateTime(start)}–${start.slice(0, 10) === end.slice(0, 10) ? end.slice(11, 16) : formatAppointmentDateTime(end)}`;
};

const SERVICE_STATUS_TEXT = Object.freeze({
    [SERVICE.WAITING_ACCEPTANCE]: "待接单",
    [SERVICE.WAITING_INVITATION_RESPONSE]: "等待邀约响应",
    [SERVICE.WAITING_INVITATION_PREREQUISITE]: "等待另一项邀约响应",
    [SERVICE.WAITING_ADMIN_REPLACEMENT]: "等待平台补位",
    [SERVICE.ACCEPTED]: "待服务",
    [SERVICE.IN_SERVICE]: "服务中",
    [SERVICE.AWAITING_PHOTOS]: "待出片",
    [SERVICE.COMPLETED]: "已完成",
    [SERVICE.CLOSED]: "已关闭",
    [SERVICE.ENDED]: "已结束",
});

// User entries describe intent, never authorization. Creator adapters keep their own action contract.
const userAction = (code, label, targetResourceType, targetResourceId) => ({ code, label, targetResourceType, targetResourceId });
const getUserAppointmentActions = (detail) => {
    const action = (code, label) => userAction(code, label, "APPOINTMENT", detail.appointmentNo);
    if (detail.appointmentStatus === APPOINTMENT.WAITING_PAYMENT) return [action("PAY", "立即支付"), action("CANCEL_APPOINTMENT", "取消预约")];
    if (detail.appointmentStatus === APPOINTMENT.PAID && [SUMMARY.WAITING_ACCEPTANCE, SUMMARY.WAITING_FULFILLMENT].includes(detail.summaryStatus)) return [action("CANCEL_APPOINTMENT", "取消预约")];
    return [];
};
const getUserServiceActions = (service) => {
    const action = (code, label) => userAction(code, label, "SERVICE_ORDER", service.serviceOrderNo);
    const cancel = () => action("CANCEL_SERVICE", "取消服务");
    const contact = () => action("CONTACT_CREATOR", service.serviceType === "MAKEUP" ? "联系化妆师" : "联系摄影师");
    const abnormal = () => action("CREATE_DISPUTE", "异常处置");
    switch (service.serviceStatus) {
    case SERVICE.WAITING_ACCEPTANCE: case SERVICE.WAITING_INVITATION_RESPONSE:
    case SERVICE.WAITING_INVITATION_PREREQUISITE: return [cancel()];
    case SERVICE.WAITING_ADMIN_REPLACEMENT: return service.serviceType === "PHOTOGRAPHY" ? [cancel()] : [];
    case SERVICE.ACCEPTED:
        if (service.invitationPrerequisitePending) return [cancel(), action("REPORT_CREATOR_NO_SHOW", "爽约"), abnormal()];
        return [cancel(), action("VIEW_SERVICE_CODE", "查看服务码"), contact(), action("REPORT_CREATOR_NO_SHOW", "爽约"), abnormal()];
    case SERVICE.IN_SERVICE: return [contact(), ...(service.checkedInAt ? [action("CONFIRM_COMPLETION", service.serviceType === "MAKEUP" ? "确认化妆完成" : "确认拍摄结束")] : []), abnormal()];
    case SERVICE.AWAITING_PHOTOS: return service.serviceType === "PHOTOGRAPHY" ? [contact(), abnormal()] : [];
    case SERVICE.COMPLETED: return [action("CREATE_AFTER_SALES", "申请售后")];
    default: return [];
    }
};

const normalizeActiveDisputes = (item) => {
    const records = new Map();
    const add = (record) => {
        if (!record || !record.disputeNo) return;
        records.set(asText(record.disputeNo), {
            disputeNo: asText(record.disputeNo), serviceOrderNo: asText(record.serviceOrderNo),
            disputeType: asText(record.disputeType), status: Number.isInteger(record.status) && record.status >= 1 && record.status <= 6 ? record.status : null,
            submittedAt: asText(record.submittedAt), closed: record.closed === true,
        });
    };
    (Array.isArray(item.activeDisputes) ? item.activeDisputes : []).forEach((record) => add(record));
    return Array.from(records.values()).sort((left, right) => left.submittedAt.localeCompare(right.submittedAt));
};

const addUserRecordActions = (detail) => {
    if (detail.refundStatus) {
        detail.actions.push(userAction("VIEW_REFUND", "查看退款", "APPOINTMENT_REFUNDS", detail.appointmentNo));
    }
    detail.services.forEach((service) => {
        service.activeDisputes = detail.activeDisputes.filter((record) => record.serviceOrderNo === service.serviceOrderNo);
        if (service.activeDisputes.length) {
            service.actions = service.actions.filter((action) => !["REPORT_CREATOR_NO_SHOW", "CREATE_DISPUTE", "CREATE_AFTER_SALES"].includes(action.code));
        }
        service.activeDisputes.forEach((record) => service.actions.push(userAction(
            "VIEW_DISPUTE", "查看处理进度", "DISPUTE", record.disputeNo,
        )));
    });
};

const getAppointmentPaymentStatusText = (status) => statusText(status, {
    [PAYMENT.CREATE_UNKNOWN]: "支付结果待确认", [PAYMENT.CREATING]: "支付准备中", [PAYMENT.PENDING]: "待支付", [PAYMENT.CREATE_FAILED]: "支付创建失败",
    [PAYMENT.CLOSED]: "已关闭", [PAYMENT.EXPIRED]: "已过期", [PAYMENT.SUCCEEDED]: "支付成功",
    [PAYMENT.LATE_SUCCEEDED]: "超时到账，原路退款", [PAYMENT.DUPLICATE_SUCCEEDED]: "重复到账，原路退款",
});

const getAppointmentRefundStatusText = (status) => statusText(status, {
    [REFUND.PROCESSING]: "退款处理中", [REFUND.SUCCEEDED]: "退款成功", [REFUND.FAILED]: "退款失败，请联系客服", [REFUND.UNKNOWN]: "退款结果确认中",
}, status == null ? "暂无退款" : "退款状态待确认");

const normalizeAppointmentRefunds = (refunds) => (Array.isArray(refunds) ? refunds : []).map((refund, index) => ({
    key: asText(refund.refundNo) || `intent-${index}`,
    refundNo: asText(refund.refundNo),
    serviceOrderNo: asText(refund.serviceOrderNo),
    serviceType: asText(refund.serviceType),
    amount: asMoney(refund.amount),
    refundStatus: numericStatus(refund.refundStatus, REFUND),
    reasonText: asText(refund.reasonText) || "退款原因暂不可用",
    createdAt: asText(refund.createdAt),
    completedAt: asText(refund.completedAt),
}));

const normalizeCurrentPaymentRequest = (request) => request ? {
    paymentRequestNo: asText(request.paymentRequestNo),
    paymentStatus: numericStatus(request.paymentStatus, PAYMENT),
    channel: asText(request.channel),
    invocationPayload: asText(request.invocationPayload),
    amount: asMoney(request.amount),
    refundNo: asText(request.refundNo),
    refundAmount: request.refundAmount == null ? null : asMoney(request.refundAmount),
    refundStatus: numericStatus(request.refundStatus, REFUND),
    expiresAt: asText(request.expiresAt),
} : null;

const normalizeCurrentProvider = (provider, serviceType, serviceStatus) => {
    if (!provider || serviceStatus === SERVICE.WAITING_ADMIN_REPLACEMENT) return null;
    const professionalType = asText(provider.professionalType).toUpperCase();
    const expectedType = serviceType === "MAKEUP" ? "MAKEUP_ARTIST" : "PHOTOGRAPHER";
    const professionalId = asText(provider.professionalId);
    if (professionalType !== expectedType || !professionalId) return null;
    const photo = provider.personalPhoto;
    return {
        professionalType,
        professionalId,
        displayName: typeof provider.displayName === "string" ? provider.displayName.trim() || null : null,
        reviewProfessionalId: asText(provider.reviewProfessionalId),
        averageScore: provider.averageScore == null ? null : Number(provider.averageScore),
        reviewCount: Number(provider.reviewCount) || 0,
        completedOrderCount: Number(provider.completedOrderCount) || 0,
        personalPhoto: photo && asText(photo.accessUrl) ? {
            accessUrl: asText(photo.accessUrl),
            expiresAt: asText(photo.expiresAt),
        } : null,
    };
};

const normalizeServiceOrder = (order = {}) => {
    const serviceType = asText(order.serviceType).toUpperCase();
    const serviceStatus = numericStatus(order.serviceStatus, SERVICE);
    const serviceStartAt = normalizeAppointmentDateTime(order.serviceStartAt);
    const serviceEndAt = normalizeAppointmentDateTime(order.serviceEndAt);
    return {
        serviceOrderNo: asText(order.serviceOrderNo),
        serviceType,
        roleLabel: serviceType === "MAKEUP" ? "化妆服务" : "摄影服务",
        serviceStatus,
        statusText: statusText(serviceStatus, SERVICE_STATUS_TEXT),
        waitingHint: order.invitationPrerequisitePending === true ? "等待另一位创作者接受邀约" : "",
        endReason: numericStatus(order.endReason, END_REASON),
        endReasonText: ({ [END_REASON.NO_ACCEPTANCE_AT_CUTOFF]: "接单截止时仍未接单", [END_REASON.PLATFORM_CANCEL]: "平台取消关联服务",
            [END_REASON.USER_CANCEL]: "用户主动取消", [END_REASON.CREATOR_CANCEL]: "创作者主动取消", [END_REASON.REPLACEMENT_NOT_ASSIGNED]: "摄影补位截止仍未接单",
            [END_REASON.CREATOR_NO_SHOW]: "创作者爽约", [END_REASON.USER_NO_SHOW]: "用户爽约", [END_REASON.RETOUCH_SELECTION_EXPIRED]: "三个月未正式选片", [END_REASON.RETOUCH_UNDELIVERABLE]: "平台核实精修无法交付" })[numericStatus(order.endReason, END_REASON)] || "结束原因待确认",
        serviceStartAt,
        serviceEndAt,
        plannedTimeText: formatServicePlan(serviceStartAt, serviceEndAt),
        expectedArrivalAt: normalizeAppointmentDateTime(order.expectedArrivalAt),
        invitationPrerequisitePending: order.invitationPrerequisitePending === true,
        currentProvider: normalizeCurrentProvider(order.currentProvider, serviceType, serviceStatus),
        reviewSummary: order.reviewSummary || null,
        acceptedAt: asText(order.acceptedAt),
        arrivedAt: asText(order.arrivedAt),
        lateMinutes: order.lateMinutes == null || !Number.isFinite(Number(order.lateMinutes)) ? null : Number(order.lateMinutes),
        checkedInAt: asText(order.checkedInAt),
        startedAt: asText(order.startedAt),
        finishedAt: asText(order.finishedAt),
    };
};

const normalizePhotographyAppointmentDetail = (item = {}) => {
    const detail = {
        appointmentNo: asText(item.appointmentNo),
        appointmentStatus: numericStatus(item.appointmentStatus, APPOINTMENT),
        summaryStatus: numericStatus(item.summaryStatus, SUMMARY),
        contactPhone: asText(item.contactPhone),
        paymentStatus: numericStatus(item.paymentStatus, PAYMENT),
        refundStatus: numericStatus(item.refundStatus, REFUND),
        paymentDeadline: asText(item.paymentDeadline),
        paymentRemainingSeconds: Math.max(0, Number(item.paymentRemainingSeconds) || 0),
        currentPaymentRequest: normalizeCurrentPaymentRequest(item.currentPaymentRequest),
        provinceName: asText(item.provinceName),
        cityName: asText(item.cityName),
        districtName: asText(item.districtName),
        themeTypeName: asText(item.themeTypeName),
        themeName: asText(item.themeName),
        styleName: asText(item.styleName),
        packageName: asText(item.packageName),
        packageBasePeople: Number(item.packageBasePeople) || 0,
        venueName: asText(item.venueName),
        pointName: asText(item.pointName),
        venueLocationSummary: asText(item.venueLocationSummary),
        peopleCount: Number(item.peopleCount) || 0,
        makeupSelected: Boolean(item.makeupSelected),
        totalAmount: asMoney(item.totalAmount),
        couponDiscountAmount: asMoney(item.couponDiscountAmount),
        payableAmount: item.payableAmount == null || item.payableAmount === "" ? "未提供" : asMoney(item.payableAmount),
        shootAt: asText(item.shootAt),
        createdAt: asText(item.createdAt),
        paidAt: asText(item.paidAt),
        closedAt: asText(item.closedAt),
        activeDisputes: normalizeActiveDisputes(item),
        services: (Array.isArray(item.serviceOrders) ? item.serviceOrders : []).map(normalizeServiceOrder),
    };
    detail.services.forEach((service) => {
        service.actions = getUserServiceActions(service);
        const assigned = item => !!item && !!item.currentProvider
            && [SERVICE.ACCEPTED, SERVICE.IN_SERVICE, SERVICE.AWAITING_PHOTOS, SERVICE.COMPLETED].includes(item.serviceStatus);
        service.serviceCodeReady = assigned(service) && assigned(detail.services.find(item => item.serviceType === 'PHOTOGRAPHY'));
        if (!service.serviceCodeReady) service.actions = service.actions.filter(action => action.code !== 'VIEW_SERVICE_CODE');
        if (!service.currentProvider) service.actions = service.actions.filter(action => action.code !== 'CONTACT_CREATOR');
    });
    detail.actions = getUserAppointmentActions(detail);
    addUserRecordActions(detail);
    detail.serviceProgress = ["PHOTOGRAPHY", "MAKEUP"].map((serviceType) => {
        const service = detail.services.find((item) => item.serviceType === serviceType);
        if (service) return service;
        return {
            serviceOrderNo: "",
            serviceType,
            roleLabel: serviceType === "MAKEUP" ? "化妆服务" : "摄影服务",
            serviceStatus: null,
            statusText: serviceType === "MAKEUP" && !detail.makeupSelected ? "未选择" : "支付后生成",
        };
    });
    return detail;
};

const normalizeCancellationPreview = (preview, appointmentNo) => {
    if (!preview || preview.appointmentNo !== appointmentNo
        || !Array.isArray(preview.serviceOrderNos) || !preview.serviceOrderNos.length
        || !Array.isArray(preview.details) || !preview.details.length) {
        throw new Error("当前取消方案不可用，请刷新状态后重试");
    }
    return {
        ...preview,
        totalRefundAmount: asMoney(preview.totalRefundAmount),
        totalDeductionAmount: asMoney(preview.totalDeductionAmount),
        compensationCouponAmount: asMoney(preview.compensationCouponAmount),
        details: preview.details.map((item) => ({ ...item, refundStatus: numericStatus(item.refundStatus, REFUND),
            serviceAmount: asMoney(item.serviceAmount), refundAmount: asMoney(item.refundAmount), deductionAmount: asMoney(item.deductionAmount),
        })),
    };
};

const normalizeCancellationResult = (result) => {
    if (!result || !result.cancellationNo) throw new Error("取消响应不完整，结果待确认");
    return {
        cancellationNo: asText(result.cancellationNo),
        totalRefundAmount: asMoney(result.totalRefundAmount),
        totalDeductionAmount: asMoney(result.totalDeductionAmount),
        details: (result.details || []).map((item) => ({ ...item, refundStatus: numericStatus(item.refundStatus, REFUND),
            serviceAmount: asMoney(item.serviceAmount), refundAmount: asMoney(item.refundAmount),
            deductionAmount: asMoney(item.deductionAmount),
        })),
    };
};

const getAppointmentStatuses = (group = "all") => (
    APPOINTMENT_GROUP_STATUSES[group] || APPOINTMENT_GROUP_STATUSES.all
).slice();

const normalizePhotographyAppointmentListItem = (item = {}) => {
    const payableAmount = item.payableAmount == null || item.payableAmount === "" ? "未提供" : asMoney(item.payableAmount);
    const paidAmount = asMoney(item.paidAmount);
    const status = numericStatus(item.displayStatus, DISPLAY);
    let paymentHint = "";
    if (status === DISPLAY.WAITING_PAYMENT) paymentHint = `待支付 ¥${payableAmount}`;
    else if (Number(paidAmount) > 0) paymentHint = `已支付 ¥${paidAmount}`;

    return {
        appointmentNo: asText(item.appointmentNo),
        themeName: asText(item.themeName),
        peopleCount: item.peopleCount == null ? null : Number(item.peopleCount),
        venueName: asText(item.venueName),
        pointName: asText(item.pointName),
        venueLocationSummary: asText(item.venueLocationSummary),
        makeupSelected: item.makeupSelected === true,
        photographyServiceStatusText: item.photographyServiceStatus == null ? "" : statusText(numericStatus(item.photographyServiceStatus, SERVICE), SERVICE_STATUS_TEXT),
        makeupServiceStatusText: item.makeupServiceStatus == null ? "" : statusText(numericStatus(item.makeupServiceStatus, SERVICE), SERVICE_STATUS_TEXT),
        styleName: asText(item.styleName),
        packageName: asText(item.packageName),
        totalAmount: asMoney(item.totalAmount),
        payableAmount,
        paidAmount,
        status,
        statusText: status == null ? "状态待确认" : asText(item.displayStatusText) || statusText(status, DISPLAY_STATUS_TEXT),
        shootAt: asText(item.shootAt),
        createdAt: asText(item.createdAt),
        paymentHint,
    };
};

const normalizePhotographyAppointmentPage = (page = {}) => ({
    items: (Array.isArray(page.items) ? page.items : []).map(normalizePhotographyAppointmentListItem),
    total: Math.max(0, Number(page.total) || 0),
    pageNo: Math.max(1, Number(page.pageNo) || 1),
    pageSize: Math.max(1, Number(page.pageSize) || 10),
});

const createPhotographyAppointmentCard = (summary = {}, detail = null) => {
    const card = summary.status !== undefined ? { ...summary } : normalizePhotographyAppointmentListItem(summary);
    if (!detail) {
        const appointmentStatus = card.status === DISPLAY.WAITING_PAYMENT ? APPOINTMENT.WAITING_PAYMENT : APPOINTMENT.PAID;
        const summaryStatus = card.status === DISPLAY.WAITING_ACCEPTANCE ? SUMMARY.WAITING_ACCEPTANCE : card.status === DISPLAY.WAITING_FULFILLMENT ? SUMMARY.WAITING_FULFILLMENT : null;
        return { ...card, services: [], actions: getUserAppointmentActions({ appointmentNo: card.appointmentNo, appointmentStatus, summaryStatus }) };
    }
    const normalizedDetail = Array.isArray(detail.services) && Array.isArray(detail.serviceProgress)
        ? detail
        : normalizePhotographyAppointmentDetail(detail);
    return {
        ...card,
        services: normalizedDetail.services,
        serviceProgress: normalizedDetail.serviceProgress,
        refundStatus: normalizedDetail.refundStatus,
        actions: normalizedDetail.actions,
    };
};

module.exports = {
    STATUS_CODES: { APPOINTMENT, SERVICE, SUMMARY, DISPLAY, PAYMENT, REFUND, END_REASON },
    getPhotographyUserActions: (detail) => detail.actions.concat(...detail.services.map((service) => service.actions)),
    normalizeCancellationResult,
    normalizeCancellationPreview,
    normalizeAppointmentRefunds,
    APPOINTMENT_GROUP_STATUSES,
    createPhotographyAppointmentCard,
    formatAppointmentDateTime,
    getAppointmentStatusText,
    getSummaryStatusText,
    getAppointmentPaymentStatusText,
    getAppointmentRefundStatusText,
    getAppointmentStatuses,
    normalizePhotographyAppointmentDetail,
    normalizePhotographyAppointmentListItem,
    normalizePhotographyAppointmentPage,
};
